import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import BottomNav from "@/components/BottomNav";
import { ArrowLeft, LogOut, KeyRound, Sparkles, RefreshCw, ShieldCheck, Pencil, Check, X as XIcon, MessageSquare, Trash2, ChevronRight, Database } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Settings() {
  const nav = useNavigate();
  const { user, updateProfile, logout, refresh } = useAuth();
  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  async function saveUsername() {
    const name = usernameDraft.trim();
    if (!name) return;
    if (name === user.username) { setEditingUsername(false); return; }
    setSavingUsername(true);
    try {
      await updateProfile({ username: name });
      toast.success("Username updated.");
      setEditingUsername(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update.");
    } finally {
      setSavingUsername(false);
    }
  }

  async function toggleAutoRenew(v) {
    try {
      await updateProfile({ auto_renew: !!v });
      toast.success(v ? "Auto-renew enabled." : "Auto-renew disabled.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed.");
    }
  }

  async function doLogout() {
    await logout();
    nav("/welcome", { replace: true });
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6">
      <header className="mb-6 flex items-center gap-3">
        <button data-testid="settings-back-btn" onClick={() => nav(-1)} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10">
          <ArrowLeft size={18} />
        </button>
        <div>
          <div className="font-display text-xs uppercase tracking-[0.35em] text-violet-400/80">Preferences</div>
          <h1 className="font-display text-3xl leading-tight text-white">Settings</h1>
        </div>
      </header>

      <Section title="Account">
        <Row label="Email" value={user?.email} />
        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
          <div className="text-xs text-neutral-500 mb-1">Username</div>
          {!editingUsername ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-white">@{user?.username}</span>
              <button
                data-testid="settings-username-edit"
                onClick={() => { setUsernameDraft(user?.username || ""); setEditingUsername(true); }}
                className="flex items-center gap-1 rounded-full bg-white/5 px-3 py-1 text-xs text-neutral-300 hover:bg-white/10 hover:text-white"
              >
                <Pencil size={11} /> Change
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                data-testid="settings-username-input"
                autoFocus
                value={usernameDraft}
                onChange={(e) => setUsernameDraft(e.target.value)}
                minLength={2}
                maxLength={30}
                className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white outline-none focus:border-violet-500/50"
              />
              <button data-testid="settings-username-save" onClick={saveUsername} disabled={savingUsername} className="rounded-lg bg-violet-500/25 px-3 py-1.5 text-violet-100 hover:bg-violet-500/35"><Check size={14} /></button>
              <button data-testid="settings-username-cancel" onClick={() => setEditingUsername(false)} className="rounded-lg bg-white/5 px-3 py-1.5 text-neutral-300 hover:bg-white/10"><XIcon size={14} /></button>
            </div>
          )}
        </div>
        {user?.is_admin && (
          <Row
            label="Role"
            value={<span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-300"><ShieldCheck size={10} /> Admin — free forever</span>}
          />
        )}
        <Link to="/reset-password" data-testid="settings-reset-password-link" className="block rounded-xl border border-white/5 bg-white/[0.02] p-3 hover:bg-white/[0.04]">
          <div className="flex items-center gap-3">
            <KeyRound size={16} className="text-neutral-400" />
            <div className="flex-1"><div className="text-sm text-white">Change password</div><div className="text-xs text-neutral-500">Go to reset form</div></div>
            <span className="text-neutral-500">→</span>
          </div>
        </Link>
        <Link to="/settings/backup" data-testid="settings-backup-link" className="block rounded-xl border border-white/5 bg-white/[0.02] p-3 hover:bg-white/[0.04]">
          <div className="flex items-center gap-3">
            <Database size={16} className="text-neutral-400" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="text-sm text-white">Backup options</div>
                {!user?.is_admin && (
                  <span
                    data-testid="settings-backup-coming-soon-badge"
                    className="rounded-full bg-amber-500/20 border border-amber-400/40 px-1.5 py-[1px] text-[9px] uppercase tracking-widest text-amber-200"
                  >
                    Coming soon
                  </span>
                )}
              </div>
              <div className="text-xs text-neutral-500">Export, restore, or import from other apps</div>
            </div>
            <span className="text-neutral-500">→</span>
          </div>
        </Link>
        <button
          onClick={() => setFeedbackOpen(true)}
          data-testid="settings-feedback-btn"
          className="w-full text-left block rounded-xl border border-white/5 bg-white/[0.02] p-3 hover:bg-white/[0.04]"
        >
          <div className="flex items-center gap-3">
            <MessageSquare size={16} className="text-neutral-400" />
            <div className="flex-1"><div className="text-sm text-white">Send feedback to creator</div><div className="text-xs text-neutral-500">Ideas or issues — 1 message per week</div></div>
            <span className="text-neutral-500">→</span>
          </div>
        </button>
      </Section>

      <Section title="Subscription">
        {user?.is_admin ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
            You're the admin/creator. All Immersive+ features are unlocked for you free, forever.
          </div>
        ) : (
          <>
            <button
              type="button"
              data-testid="settings-plan-card"
              onClick={() => setPlanOpen(true)}
              className={`w-full text-left rounded-2xl border p-4 transition ${
                user?.is_plus
                  ? "border-violet-500/30 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/5 hover:from-violet-500/15"
                  : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
              }`}
            >
              <div className="flex items-center gap-3">
                <Sparkles size={18} className={user?.is_plus ? "text-violet-200" : "text-neutral-400"} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">
                    {user?.is_plus ? "Immersive+" : "Free plan"}
                  </div>
                  <div className="text-xs text-neutral-400">
                    {user?.is_plus
                      ? "Unlimited everything. Tap to compare plans or cancel."
                      : "Basic access with limits. Tap to compare plans and upgrade."}
                  </div>
                </div>
                <ChevronRight size={16} className="text-neutral-500" />
              </div>
            </button>
            {user?.is_plus && user?.plus_expires_at && (
              <Row label="Renews" value={new Date(user.plus_expires_at).toLocaleDateString()} />
            )}
            {user?.is_plus && (
              <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-3">
                <div>
                  <div className="text-sm text-white flex items-center gap-2"><RefreshCw size={12} /> Auto-renew</div>
                  <div className="text-xs text-neutral-500">Automatically renew Immersive+ every 30 days.</div>
                </div>
                <Switch
                  data-testid="settings-auto-renew"
                  checked={!!user?.auto_renew}
                  onCheckedChange={toggleAutoRenew}
                />
              </div>
            )}
          </>
        )}
      </Section>

      <button
        onClick={doLogout}
        data-testid="settings-logout-btn"
        className="btn-pill w-full mt-4 py-3 bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/15"
      >
        <LogOut size={14} /> Sign out
      </button>

      <button
        onClick={() => setDeleteOpen(true)}
        data-testid="settings-delete-account-btn"
        className="btn-pill w-full mt-2 py-3 bg-transparent border border-rose-500/50 text-rose-400 hover:bg-rose-500/5"
      >
        <Trash2 size={14} /> Delete account permanently
      </button>

      <BottomNav />
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <PlanModal
        open={planOpen}
        onOpenChange={setPlanOpen}
        isPlus={!!user?.is_plus}
        onCanceled={async () => { await refresh(); setPlanOpen(false); }}
      />
      <DeleteAccountModal open={deleteOpen} onOpenChange={setDeleteOpen} onDeleted={async () => {
        localStorage.removeItem("ica_token");
        await logout();
        nav("/welcome", { replace: true });
      }} />
    </div>
  );
}

function PlanModal({ open, onOpenChange, isPlus, onCanceled }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const freeFeatures = [
    "Up to 2 characters, 2 personas",
    "2 message regenerations per bot reply",
    "5 story recaps per month",
    "30 messages per day",
    "SFW & 18+ characters (with age verification)",
    "Full custom character creation",
  ];
  const plusFeatures = [
    "Unlimited characters & personas",
    "Unlimited regenerations & story recaps",
    "Unlimited chatting — no daily message cap",
    "Priority streaming speed",
    "Early access to new features (group chats, wallpapers)",
    "Support the creator directly",
  ];

  async function cancel() {
    if (!confirm("Cancel Immersive+ and switch back to the free plan?")) return;
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Switched to the free plan.");
      onCanceled && onCanceled();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed.");
    } finally {
      setBusy(false);
    }
  }

  function goUpgrade() {
    onOpenChange(false);
    nav("/upgrade");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 w-[calc(100vw-1rem)] sm:w-auto sm:max-w-2xl max-h-[92vh] overflow-y-auto text-white p-4 sm:p-6" data-testid="plan-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Choose your plan</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <PlanCard
            testId="plan-card-free"
            title="Free"
            price="$0"
            tagline="Great for testing the waters."
            features={freeFeatures}
            active={!isPlus}
            action={
              isPlus ? (
                <button
                  onClick={cancel}
                  disabled={busy}
                  data-testid="plan-switch-free-btn"
                  className="btn-pill w-full bg-white/5 border border-white/10 text-white hover:bg-white/10 disabled:opacity-50"
                >
                  {busy ? "Switching…" : "Switch to Free"}
                </button>
              ) : (
                <div data-testid="plan-current-free" className="text-center text-xs text-emerald-300 font-medium">
                  Current plan
                </div>
              )
            }
          />
          <PlanCard
            testId="plan-card-plus"
            title="Immersive+"
            price="$14.99/mo"
            tagline="Unlimited storytelling."
            features={plusFeatures}
            highlight
            active={isPlus}
            action={
              isPlus ? (
                <div data-testid="plan-current-plus" className="text-center text-xs text-violet-200 font-medium">
                  Current plan
                </div>
              ) : (
                <button
                  onClick={goUpgrade}
                  data-testid="plan-upgrade-btn"
                  className="btn-pill w-full btn-primary"
                >
                  Upgrade to Immersive+
                </button>
              )
            }
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlanCard({ testId, title, price, tagline, features, highlight, active, action }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-4 flex flex-col gap-3 ${
        highlight
          ? "border-violet-500/40 bg-gradient-to-b from-violet-500/10 to-fuchsia-500/5"
          : "border-white/10 bg-white/[0.02]"
      } ${active ? "ring-1 ring-white/20" : ""}`}
    >
      <div>
        <div className="flex items-baseline gap-2">
          <h3 className="font-display text-xl text-white">{title}</h3>
          {active && <span className="text-[10px] uppercase tracking-widest text-neutral-400">Active</span>}
        </div>
        <div className="mt-0.5 text-2xl font-display text-white">{price}</div>
        <div className="mt-1 text-xs text-neutral-400">{tagline}</div>
      </div>
      <ul className="space-y-1.5 text-sm text-neutral-200 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={14} className={highlight ? "text-violet-300 mt-0.5 shrink-0" : "text-emerald-300 mt-0.5 shrink-0"} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {action}
    </div>
  );
}

function DeleteAccountModal({ open, onOpenChange, onDeleted }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  async function del() {
    if (!confirmed) return toast.error("Check the confirmation box first.");
    if (!password) return toast.error("Enter your password.");
    setBusy(true);
    try {
      await api.delete("/auth/me", { data: { password } });
      toast.success("Account deleted.");
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-md text-white" data-testid="delete-account-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl text-rose-300">Delete account?</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-100">
            This is <strong>permanent</strong>. All your characters, personas, chats, messages, and feedback will be deleted immediately and cannot be recovered.
          </div>
          <label className="flex items-start gap-3">
            <input type="checkbox" data-testid="delete-confirm-check" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1 accent-rose-500" />
            <span className="text-sm">I understand this cannot be undone.</span>
          </label>
          <label>
            <span className="mb-1 block text-xs text-neutral-400">Confirm your password</span>
            <input type="password" data-testid="delete-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white outline-none focus:border-rose-500/60" />
          </label>
          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} className="btn-pill btn-ghost flex-1">Cancel</button>
            <button data-testid="delete-final-btn" onClick={del} disabled={busy} className="btn-pill flex-1 bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-50">
              {busy ? "Deleting…" : "Delete forever"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackModal({ open, onOpenChange }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  async function submit() {
    if (!subject.trim() || !body.trim()) return toast.error("Fill in both fields.");
    setSending(true);
    try {
      await api.post("/feedback", { subject, body });
      toast.success("Feedback sent — thank you!");
      setSubject(""); setBody("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send.");
    } finally {
      setSending(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-md text-white" data-testid="feedback-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl">Send feedback</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-neutral-400">A message to the app's creator. Bug reports, feature requests, kind words — all welcome. Limited to one per week per user.</p>
          <input data-testid="feedback-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" maxLength={120} className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white outline-none focus:border-violet-500/50" />
          <textarea data-testid="feedback-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Your message…" rows={5} maxLength={3000} className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white outline-none focus:border-violet-500/50" />
          <button data-testid="feedback-send-btn" onClick={submit} disabled={sending} className="btn-pill btn-primary w-full py-2.5">
            {sending ? "Sending…" : "Send to creator"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-xs uppercase tracking-[0.25em] text-neutral-500">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-3">
      <span className="text-xs text-neutral-500">{label}</span>
      <span className="text-sm text-white truncate ml-4">{value}</span>
    </div>
  );
}
